import React, { useEffect, useState } from "react";
import { Trash2, Send, UserPlus, KeyRound, MailCheck, Clock } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const field = "w-full bg-white border border-[#E5E5E5] p-2.5 font-body text-xs text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md";
const primary = "w-full font-body font-bold text-xs uppercase tracking-wide py-3 bg-[#2E3A9E] text-white hover:bg-[#232D7C] rounded-md disabled:opacity-60 flex items-center justify-center gap-2";

export default function AdminTeam({ notify }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invite, setInvite] = useState({ name: "", email: "" });
  const [inviteState, setInviteState] = useState({ loading: false, error: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwState, setPwState] = useState({ loading: false, error: "", ok: false });

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviteState({ loading: true, error: "" });
    try {
      await api.post("/auth/invite", invite);
      setInvite({ name: "", email: "" }); load(); notify("Invitation email sent");
      setInviteState({ loading: false, error: "" });
    } catch (err) {
      load();
      setInviteState({ loading: false, error: formatApiError(err) });
    }
  };

  const resend = async (u) => {
    try { await api.post(`/auth/users/${u.id}/resend-invite`); notify("Invitation re-sent"); }
    catch (err) { notify(formatApiError(err), "error"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); load(); notify("User removed"); }
    catch (err) { notify(formatApiError(err), "error"); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return setPwState({ loading: false, error: "New passwords do not match.", ok: false });
    setPwState({ loading: true, error: "", ok: false });
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      setPwState({ loading: false, error: "", ok: true }); notify("Password updated");
    } catch (err) {
      setPwState({ loading: false, error: formatApiError(err), ok: false });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="admin-team">
      <div className="lg:col-span-4 space-y-8">
        <form onSubmit={sendInvite} className="bg-white border border-[#E5E5E5] p-6 rounded-md shadow-sm relative space-y-3">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#6B1414]" />
          <h4 className="font-heading text-lg font-bold flex items-center gap-2"><UserPlus size={18} /> Invite a team member</h4>
          <p className="font-body text-xs text-gray-500">They receive an email to verify their address and choose a password. Until calium.in is verified in Resend, invites can only reach info@calium.in.</p>
          <input data-testid="invite-name-input" required placeholder="Full name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className={field} />
          <input data-testid="invite-email-input" required type="email" placeholder="Work email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className={field} />
          {inviteState.error && <p data-testid="invite-error" className="font-body text-xs text-[#6B1414] bg-red-50 border border-red-200 rounded-md p-2">{inviteState.error}</p>}
          <button data-testid="invite-submit-btn" disabled={inviteState.loading} className={primary}><Send size={14} /> {inviteState.loading ? "Sending..." : "Send invitation"}</button>
        </form>

        <form onSubmit={changePw} className="bg-white border border-[#E5E5E5] p-6 rounded-md shadow-sm relative space-y-3">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#2E3A9E]" />
          <h4 className="font-heading text-lg font-bold flex items-center gap-2"><KeyRound size={18} /> Change my password</h4>
          <input data-testid="pw-current-input" required type="password" placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className={field} />
          <input data-testid="pw-new-input" required type="password" minLength={8} placeholder="New password (min 8)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className={field} />
          <input data-testid="pw-confirm-input" required type="password" placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={field} />
          {pwState.error && <p data-testid="pw-error" className="font-body text-xs text-[#6B1414] bg-red-50 border border-red-200 rounded-md p-2">{pwState.error}</p>}
          {pwState.ok && <p data-testid="pw-success" className="font-body text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2">Password updated. Use it next time you sign in.</p>}
          <button data-testid="pw-submit-btn" disabled={pwState.loading} className={primary}>{pwState.loading ? "Saving..." : "Update password"}</button>
        </form>
      </div>

      <div className="lg:col-span-8 bg-white border border-[#E5E5E5] rounded-md shadow-sm overflow-hidden">
        <div className="bg-gray-50 border-b border-[#E5E5E5] p-4 flex justify-between items-center">
          <span className="font-mono text-xs uppercase tracking-wider font-bold">Control Room accounts</span>
          <span className="font-mono text-[10px] text-gray-400">{users.length} accounts</span>
        </div>
        <ul className="divide-y divide-[#E5E5E5]">
          {users.map((u) => (
            <li key={u.id} data-testid="team-user-row" className="p-4 flex items-center justify-between gap-4 text-xs font-body">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-9 h-9 rounded-full bg-[#0A0A0A] text-[#F5D5B0] font-heading font-black flex items-center justify-center shrink-0">{(u.name || u.email)[0]?.toUpperCase()}</span>
                <div className="min-w-0">
                  <p className="font-bold text-[#0A0A0A] truncate">{u.name} {u.id === user?.id && <span className="font-mono text-[9px] text-gray-400">(you)</span>}</p>
                  <p className="text-gray-500 truncate">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span data-testid="team-user-status" className={`inline-flex items-center gap-1 font-mono text-[9px] uppercase px-2 py-1 rounded border ${u.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-yellow-50 text-yellow-700 border-yellow-200"}`}>
                  {u.status === "active" ? <MailCheck size={10} /> : <Clock size={10} />} {u.status === "active" ? "Verified" : "Invite pending"}
                </span>
                {u.status !== "active" && <button data-testid="team-resend-btn" onClick={() => resend(u)} title="Resend invitation" className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded"><Send size={14} /></button>}
                {u.id !== user?.id && <button data-testid="team-delete-btn" onClick={() => remove(u)} title="Remove" className="p-2 border border-red-200 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
